import type { ComponentType } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { History, Images, Zap } from 'lucide-react';
import { FacebookIcon, GoogleBusinessIcon, InstagramIcon, LinkedInIcon, PLATFORM_ICON_SRC } from '../../components/PlatformIcon';

type TabIcon = ComponentType<{ className?: string }>;
import { useBusiness } from '../../contexts/BusinessContext';
import { SOCIAL_ENABLED } from '../../lib/featureFlags';
import { supabase } from '../../lib/supabaseClient';
import GoogleBusinessWorkspace from '../../components/GoogleBusinessWorkspace';
import GoogleBusinessHistory from '../../components/GoogleBusinessHistory';
import GoogleBusinessAutomateSettings from '../../components/GoogleBusinessAutomateSettings';
import SocialAutomateSettings from '../../components/SocialAutomateSettings';
import FacebookWorkspace from '../../components/FacebookWorkspace';
import type { SocialPlatform } from '../../components/SocialConnectButton';
import MediaManagerLibrary from '../../components/MediaManagerLibrary';
import { MarketingFace } from '../../components/NexusFace';
import { DUTY_CATALOGUE, normalizeDuties, type Duties } from '../../components/agents/types';

type MediaTab =
  | 'automate'
  | 'media'
  | 'google-business'
  | 'facebook'
  | 'instagram'
  | 'linkedin'
  | 'history';
type AutomateView = 'list' | 'google-business-settings' | 'social-settings';
type GbpWorkspaceTab = 'reviews' | 'posts' | 'photos';

type GbpLocationCard = {
  id: string;
  locationName: string | null;
  autoReplyOn: boolean;
  autoPostsOn: boolean;
};

type SocialChannelCard = {
  id: string;
  platform: SocialPlatform;
  displayName: string | null;
  autoPostsOn: boolean;
};

type MarketingManagerCard = {
  id: string;
  name: string;
  paused: boolean;
  duties: Duties;
};

const BASE_TABS: { id: MediaTab; label: string; icon: TabIcon }[] = [
  { id: 'media', label: 'Media', icon: Images },
  { id: 'automate', label: 'Automate', icon: Zap },
];

const HISTORY_TAB: { id: MediaTab; label: string; icon: TabIcon } = {
  id: 'history',
  label: 'History',
  icon: History,
};

function readMediaDeepLink(search: string): {
  tab: MediaTab | null;
  gbpTab: GbpWorkspaceTab | null;
} {
  const params = new URLSearchParams(search);
  const view = params.get('view');
  const gbpTabRaw = params.get('gbpTab');
  const tab: MediaTab | null =
    view === 'google-business' ||
    view === 'media' ||
    view === 'automate' ||
    view === 'history' ||
    view === 'facebook' ||
    view === 'instagram' ||
    view === 'linkedin'
      ? view
      : null;
  const gbpTab: GbpWorkspaceTab | null =
    gbpTabRaw === 'photos' || gbpTabRaw === 'posts' || gbpTabRaw === 'reviews' ? gbpTabRaw : null;
  return { tab, gbpTab };
}

export default function MediaManager({
  embedded = false,
  hideChrome = false,
}: {
  embedded?: boolean;
  /** Hide the "Media Manager" header + tab strip — view is driven by ?view= only. */
  hideChrome?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const deepLink = useMemo(() => readMediaDeepLink(location.search), [location.search]);
  const [activeTab, setActiveTab] = useState<MediaTab>(() => deepLink.tab || 'media');
  const [gbpInitialTab, setGbpInitialTab] = useState<GbpWorkspaceTab | undefined>(
    () => deepLink.gbpTab || undefined,
  );

  const selectMediaTab = useCallback(
    (tab: MediaTab) => {
      setActiveTab(tab);
      if (!embedded) return;
      const params = new URLSearchParams(location.search);
      params.delete('page');
      if (tab === 'media') {
        params.delete('view');
      } else {
        params.set('view', tab);
      }
      if (tab !== 'google-business') {
        params.delete('gbpTab');
      }
      const search = params.toString();
      navigate(search ? `/marketing?${search}` : '/marketing', { replace: true });
    },
    [embedded, location.search, navigate],
  );
  const [automateView, setAutomateView] = useState<AutomateView>('list');
  const [gbpConnected, setGbpConnected] = useState<boolean | null>(null);
  const [channelsLoaded, setChannelsLoaded] = useState(false);
  // The Marketing manager bot owns automation now. null until loaded; undefined when the business has none.
  const [manager, setManager] = useState<MarketingManagerCard | null | undefined>(null);
  const [locations, setLocations] = useState<GbpLocationCard[]>([]);
  const [socialChannels, setSocialChannels] = useState<SocialChannelCard[]>([]);
  const [selectedAutomateConnectionId, setSelectedAutomateConnectionId] = useState<string | null>(
    null,
  );
  const [selectedSocialPlatform, setSelectedSocialPlatform] = useState<SocialPlatform | null>(null);

  const facebookConnected = socialChannels.some((c) => c.platform === 'facebook');
  const instagramConnected = socialChannels.some((c) => c.platform === 'instagram');
  const linkedinConnected = socialChannels.some((c) => c.platform === 'linkedin');

  const visibleTabs = useMemo(() => {
    const channelTabs: { id: MediaTab; label: string; icon: TabIcon }[] = [];
    // Wait until GBP status is known so we don't flash-hide the tab on load.
    if (gbpConnected === true) {
      channelTabs.push({
        id: 'google-business',
        label: 'Google Business',
        icon: GoogleBusinessIcon,
      });
    }
    if (SOCIAL_ENABLED && facebookConnected) {
      channelTabs.push({ id: 'facebook', label: 'Facebook', icon: FacebookIcon });
    }
    if (SOCIAL_ENABLED && instagramConnected) {
      channelTabs.push({ id: 'instagram', label: 'Instagram', icon: InstagramIcon });
    }
    if (SOCIAL_ENABLED && linkedinConnected) {
      channelTabs.push({ id: 'linkedin', label: 'LinkedIn', icon: LinkedInIcon });
    }
    return [...BASE_TABS, ...channelTabs, HISTORY_TAB];
  }, [gbpConnected, facebookConnected, instagramConnected, linkedinConnected]);

  // If deep-link / active tab targets a disconnected channel, fall back to Media.
  useEffect(() => {
    if (!channelsLoaded) return;
    const stillVisible = visibleTabs.some((t) => t.id === activeTab);
    if (!stillVisible) selectMediaTab('media');
  }, [channelsLoaded, visibleTabs, activeTab, selectMediaTab]);

  useEffect(() => {
    if (deepLink.tab) setActiveTab(deepLink.tab);
    if (deepLink.gbpTab) setGbpInitialTab(deepLink.gbpTab);
  }, [deepLink.tab, deepLink.gbpTab]);

  const refreshGbpLocations = useCallback(async () => {
    if (!businessId) {
      setGbpConnected(false);
      setLocations([]);
      return;
    }

    const { data: conns } = await supabase
      .from('google_business_connections')
      .select('id, location_name')
      .eq('business_id', businessId)
      .in('provider', ['ayrshare', 'google'])
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    const rows = conns || [];
    setGbpConnected(rows.length > 0);

    if (rows.length === 0) {
      setLocations([]);
      return;
    }

    const ids = rows.map((r) => r.id);
    const [{ data: replyRows }, { data: postRows }] = await Promise.all([
      supabase
        .from('google_business_auto_reply_settings')
        .select('connection_id, enabled')
        .in('connection_id', ids),
      supabase
        .from('google_business_auto_post_settings')
        .select('connection_id, enabled')
        .in('connection_id', ids),
    ]);

    const replyOn = new Set(
      (replyRows || []).filter((r) => r.enabled).map((r) => r.connection_id as string),
    );
    const postsOn = new Set(
      (postRows || []).filter((r) => r.enabled).map((r) => r.connection_id as string),
    );

    setLocations(
      rows.map((r) => ({
        id: r.id,
        locationName: r.location_name,
        autoReplyOn: replyOn.has(r.id),
        autoPostsOn: postsOn.has(r.id),
      })),
    );
  }, [businessId]);

  const refreshSocialChannels = useCallback(async () => {
    if (!SOCIAL_ENABLED || !businessId) {
      setSocialChannels([]);
      return;
    }
    const { data: conns } = await supabase
      .from('social_media_connections')
      .select('id, platform, display_name, username')
      .eq('business_id', businessId)
      .eq('provider', 'ayrshare')
      .eq('is_active', true)
      .in('platform', ['facebook', 'instagram', 'linkedin'])
      .order('platform', { ascending: true });

    const rows = conns || [];
    if (rows.length === 0) {
      setSocialChannels([]);
      return;
    }
    const ids = rows.map((r) => r.id);
    const { data: postRows } = await supabase
      .from('social_auto_post_settings')
      .select('connection_id, enabled')
      .in('connection_id', ids);
    const postsOn = new Set(
      (postRows || []).filter((r) => r.enabled).map((r) => r.connection_id as string),
    );
    setSocialChannels(
      rows.map((r) => ({
        id: r.id as string,
        platform: r.platform as SocialPlatform,
        displayName: (r.display_name || r.username || null) as string | null,
        autoPostsOn: postsOn.has(r.id as string),
      })),
    );
  }, [businessId]);

  const refreshManager = useCallback(async () => {
    if (!businessId) { setManager(undefined); return; }
    const read = async () => {
      const { data } = await supabase
        .from('agent_bots')
        .select('id, name, paused, duties')
        .eq('business_id', businessId)
        .eq('kind', 'marketing')
        .is('archived_at', null)
        .maybeSingle();
      return data as { id: string; name: string; paused: boolean | null; duties: unknown } | null;
    };
    let row = await read();
    if (!row) {
      // Marketing is installed (we are inside it), so the bot should exist; create it if not.
      const { error } = await supabase.rpc('ensure_my_marketing_bot', { p_business_id: businessId });
      if (!error) row = await read();
    }
    setManager(row ? { id: row.id, name: row.name || 'Marketing', paused: row.paused !== false, duties: normalizeDuties(row.duties) } : undefined);
  }, [businessId]);

  useEffect(() => {
    void refreshManager();
  }, [refreshManager]);

  useEffect(() => {
    let cancelled = false;
    setChannelsLoaded(false);
    void (async () => {
      await Promise.all([refreshGbpLocations(), refreshSocialChannels()]);
      if (!cancelled) setChannelsLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshGbpLocations, refreshSocialChannels]);

  useEffect(() => {
    if (activeTab === 'automate' && automateView === 'list') {
      void refreshGbpLocations();
      void refreshSocialChannels();
      void refreshManager();
    }
  }, [gbpConnected, activeTab, automateView, refreshGbpLocations, refreshSocialChannels, refreshManager]);

  // Old per-channel automations only show while they are still running and the manager is off.
  // Once the manager is on it owns all of this and the old settings are switched off.
  const legacyRunning =
    manager !== null && (!manager || manager.paused) &&
    (locations.some((l) => l.autoReplyOn || l.autoPostsOn) || socialChannels.some((c) => c.autoPostsOn));

  useEffect(() => {
    if (activeTab !== 'automate') {
      setAutomateView('list');
      setSelectedAutomateConnectionId(null);
      setSelectedSocialPlatform(null);
    }
  }, [activeTab]);

  const selectedLocation = locations.find((l) => l.id === selectedAutomateConnectionId) || null;
  const selectedSocial =
    socialChannels.find((c) => c.id === selectedAutomateConnectionId) || null;

  return (
    <div
      id={embedded ? 'marketing-media-manager' : undefined}
      className={
        embedded
          ? 'flex flex-col bg-white rounded-[14px] border border-[#e8eaed] overflow-hidden'
          : 'h-full flex flex-col bg-gray-50'
      }
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      {hideChrome ? null : (
      <div className="border-b border-gray-200/60 bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-6 pt-3 sm:pt-3.5 pb-0">
          <h2
            className="text-[13px] font-semibold uppercase tracking-[0.04em] text-gray-400"
            style={{ letterSpacing: '0.04em' }}
          >
            Media Manager
          </h2>
        </div>

        <nav
          className="flex overflow-x-auto scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="flex min-w-max px-4 sm:px-6">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => selectMediaTab(tab.id)}
                className={`flex items-center justify-center space-x-1.5 sm:space-x-2 py-3 px-3 sm:px-4 border-b-2 font-semibold text-[13px] sm:text-sm transition-all duration-150 whitespace-nowrap flex-shrink-0 touch-manipulation ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                    : 'border-transparent text-gray-600 active:text-gray-900 active:bg-gray-50'
                }`}
                style={{
                  minHeight: '44px',
                  WebkitTapHighlightColor: 'transparent',
                  letterSpacing: '-0.01em',
                }}
              >
                <tab.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>
      )}

      <div className={embedded ? '' : 'flex-1 overflow-y-auto'}>
        {activeTab === 'automate' && automateView === 'list' && (
          <div className="p-4 sm:p-6 max-w-4xl">
            <div className="mb-5">
              <h2
                className="text-base font-semibold text-gray-900"
                style={{ letterSpacing: '-0.01em' }}
              >
                Automations
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Your Marketing manager runs replies, reviews, posting, and reporting. Decide what it does on its own.
              </p>
            </div>

            {manager !== null && (
              <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                {manager ? (() => {
                  const on = DUTY_CATALOGUE.filter((d) => manager.duties[d.key].on);
                  const auto = on.filter((d) => d.hasMode && manager.duties[d.key].mode === 'auto');
                  return (
                    <div className="flex items-start gap-3.5">
                      <div className={`shrink-0 rounded-2xl bg-zinc-950 p-1.5 ${manager.paused ? 'opacity-50 grayscale' : ''}`}>
                        <MarketingFace size={44} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                            {manager.name} · Marketing manager
                          </h3>
                          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md border ${manager.paused ? 'text-gray-600 bg-gray-50 border-gray-200' : 'text-pink-700 bg-pink-50 border-pink-200'}`}>
                            {manager.paused ? 'Off' : 'Working'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          {manager.paused
                            ? 'Switched off. Turn it on to have it reply to comments and Google reviews, remove spam, keep your channels posting, and send you a weekly report. You will see everything it does before it starts.'
                            : `${on.length} of ${DUTY_CATALOGUE.length} duties on${auto.length ? `, ${auto.length} running without asking` : ''}: ${on.map((d) => d.title.replace(/^Reply to /, '').replace(/^Keep your channels posting$/, 'scheduled posts').replace(/^Watch /, '').toLowerCase()).join(', ')}.`}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/agents?bot=${manager.id}${manager.paused ? '&turnon=1' : ''}`)}
                            className={`inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold touch-manipulation ${manager.paused ? 'bg-pink-600 text-white hover:bg-pink-500' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            {manager.paused ? 'Turn on' : 'Manage duties'}
                          </button>
                          {!manager.paused && (
                            <button
                              type="button"
                              onClick={() => navigate(`/agents?bot=${manager.id}&open=chat`)}
                              className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-gray-300 touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Open chat
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })() : (
                  <p className="text-sm text-gray-600">Install the Marketing app to get your Marketing manager.</p>
                )}
              </div>
            )}

            {legacyRunning && (
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Running from the old settings</h3>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                  These keep working until you turn the Marketing manager on; it then takes over and these switch off.
                </p>
              </div>
            )}

            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 ${legacyRunning ? '' : 'hidden'}`}>
              {gbpConnected &&
                locations.map((loc) => (
                  <button
                    key={loc.id}
                    type="button"
                    onClick={() => {
                      setSelectedAutomateConnectionId(loc.id);
                      setSelectedSocialPlatform(null);
                      setAutomateView('google-business-settings');
                    }}
                    className="text-left bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:border-gray-300 hover:shadow-sm transition-all touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-start gap-3">
                      <img
                        src={PLATFORM_ICON_SRC.google_business}
                        alt=""
                        className="w-11 h-11 rounded-xl shadow-sm flex-shrink-0 object-contain"
                        draggable={false}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3
                            className="text-sm font-semibold text-gray-900 truncate"
                            style={{ letterSpacing: '-0.01em' }}
                          >
                            {loc.locationName || 'Google Business location'}
                          </h3>
                          <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" aria-hidden />
                          {loc.autoReplyOn && (
                            <span className="text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-md">
                              Auto-reply
                            </span>
                          )}
                          {loc.autoPostsOn && (
                            <span className="text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-md">
                              Auto-posts
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          Automate AI review replies and posts for this location
                        </p>
                      </div>
                    </div>
                  </button>
                ))}

              {SOCIAL_ENABLED &&
                socialChannels.map((channel) => {
                  const label =
                    channel.platform === 'facebook'
                      ? 'Facebook'
                      : channel.platform === 'linkedin'
                        ? 'LinkedIn'
                        : 'Instagram';
                  const Icon =
                    channel.platform === 'facebook'
                      ? FacebookIcon
                      : channel.platform === 'linkedin'
                        ? LinkedInIcon
                        : InstagramIcon;
                  return (
                    <button
                      key={channel.id}
                      type="button"
                      onClick={() => {
                        setSelectedAutomateConnectionId(channel.id);
                        setSelectedSocialPlatform(channel.platform);
                        setAutomateView('social-settings');
                      }}
                      className="text-left bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:border-gray-300 hover:shadow-sm transition-all touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm flex-shrink-0 overflow-hidden">
                          <Icon className="h-11 w-11" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3
                              className="text-sm font-semibold text-gray-900 truncate"
                              style={{ letterSpacing: '-0.01em' }}
                            >
                              {channel.displayName || label}
                            </h3>
                            <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" aria-hidden />
                            {channel.autoPostsOn && (
                              <span className="text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-md">
                                Auto-posts
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                            Automate AI posts for {label}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}

            </div>
            {!gbpConnected && socialChannels.length === 0 && channelsLoaded ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center">
                <p className="text-sm text-gray-600">
                  Connect a channel so your Marketing manager has somewhere to work
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/app-store?tab=integrations')}
                  className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-700"
                >
                  Open Integrations
                </button>
              </div>
            ) : null}
          </div>
        )}

        {activeTab === 'automate' &&
          automateView === 'google-business-settings' &&
          selectedAutomateConnectionId && (
            <div className="p-4 sm:p-6 max-w-xl">
              <GoogleBusinessAutomateSettings
                connectionId={selectedAutomateConnectionId}
                locationName={selectedLocation?.locationName || undefined}
                onBack={() => {
                  setAutomateView('list');
                  setSelectedAutomateConnectionId(null);
                  void refreshGbpLocations();
                  void refreshSocialChannels();
                }}
              />
            </div>
          )}

        {activeTab === 'automate' &&
          automateView === 'social-settings' &&
          selectedAutomateConnectionId &&
          selectedSocialPlatform && (
            <div className="p-4 sm:p-6 max-w-xl">
              <SocialAutomateSettings
                platform={selectedSocialPlatform}
                connectionId={selectedAutomateConnectionId}
                displayName={selectedSocial?.displayName || undefined}
                onBack={() => {
                  setAutomateView('list');
                  setSelectedAutomateConnectionId(null);
                  setSelectedSocialPlatform(null);
                  void refreshSocialChannels();
                }}
              />
            </div>
          )}

        {activeTab === 'media' && (
          <div className="p-4 sm:p-6">
            <div className="mb-4">
              <h2
                className="text-base font-semibold text-gray-900"
                style={{ letterSpacing: '-0.01em' }}
              >
                Media library
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Upload and manage images for posts and future automations
              </p>
            </div>
            <MediaManagerLibrary />
          </div>
        )}

        {activeTab === 'google-business' && (
          <div className="p-4 sm:p-6 max-w-4xl">
            <GoogleBusinessWorkspace
              initialTab={gbpInitialTab}
              onConnectionChange={(c) => setGbpConnected(c)}
            />
          </div>
        )}

        {activeTab === 'facebook' && SOCIAL_ENABLED && (
          <div className="p-4 sm:p-6 max-w-5xl">
            <FacebookWorkspace
              platform="facebook"
              onConnectionChange={() => void refreshSocialChannels()}
            />
          </div>
        )}

        {activeTab === 'instagram' && SOCIAL_ENABLED && (
          <div className="p-4 sm:p-6 max-w-5xl">
            <FacebookWorkspace
              platform="instagram"
              onConnectionChange={() => void refreshSocialChannels()}
            />
          </div>
        )}

        {activeTab === 'linkedin' && SOCIAL_ENABLED && (
          <div className="p-4 sm:p-6 max-w-5xl">
            <FacebookWorkspace
              platform="linkedin"
              onConnectionChange={() => void refreshSocialChannels()}
            />
          </div>
        )}

        {activeTab === 'history' && (
          <div className="p-4 sm:p-6 max-w-4xl">
            <div className="mb-4">
              <h2
                className="text-base font-semibold text-gray-900"
                style={{ letterSpacing: '-0.01em' }}
              >
                History
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Replies and posts from Google Business
                {SOCIAL_ENABLED ? ', Facebook, Instagram, and LinkedIn' : ''}
              </p>
            </div>
            <GoogleBusinessHistory />
          </div>
        )}
      </div>
    </div>
  );
}
